/**
 * Chart replay: a per-tab cursor that becomes the tab's "now".
 *
 * The chart shows bars up to `cursorTime` and nothing after it, so drawings,
 * indicators and analysis can't see the outcome of the setup being studied.
 * The truncation itself happens in ONE place — `useTabOhlc` (state/hooks.ts),
 * the only reader of `state.ohlc` — so no renderer knows replay exists.
 *
 * This state is deliberately EPHEMERAL: it is never written to the workspace
 * payload, and `hydrateWorkspace` clears it. A persisted cursor would ride the
 * live-sync poke to the user's other sessions and rewind a chart someone is
 * watching live. Replay is a mode you are in right now, not a document.
 *
 * See features/chart_replay.md.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { removeTab } from "./tabsSlice";
import { hydrateWorkspace } from "./workspacesSlice";

/** Playback speeds, in BARS PER SECOND — not compressed wall-clock. One bar a
 *  second means the same thing on 1min and 1D, which is what "step through the
 *  chart" means to a chart reader. */
export const REPLAY_SPEEDS = [1, 2, 5, 10, 50] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];

/** Which affordance started the replay, reported to analytics. Only the
 *  toolbar's start picker exists today; the field stays because a date picker
 *  is the next planned way in and the comparison is the point. */
export type ReplayEntry = "picker";

export interface ReplayTabState {
  /** Picking a start bar: the chart shows a vertical line tracking the cursor
   *  and the next click begins the replay there. A separate flag from
   *  `active` because nothing is truncated yet — the user is still looking at
   *  the whole chart while choosing where to rewind to. */
  selecting: boolean;
  active: boolean;
  /** UTC seconds of the bar the cursor sits on — the newest bar the chart may
   *  show. Always a real bar's time (callers clamp via `clampToBar`). */
  cursorTime: number | null;
  /** Where the cursor started. Kept so "how far did this replay run" is a
   *  subtraction rather than a guess. */
  startCursorTime: number | null;
  entry: ReplayEntry | null;
  playing: boolean;
  speed: ReplaySpeed;
}

interface ReplayState {
  byTab: Record<string, ReplayTabState>;
}

const initialState: ReplayState = { byTab: {} };

export function createDefaultReplayTabState(): ReplayTabState {
  return {
    selecting: false,
    active: false,
    cursorTime: null,
    startCursorTime: null,
    entry: null,
    playing: false,
    speed: 1,
  };
}

const replaySlice = createSlice({
  name: "replay",
  initialState,
  reducers: {
    /** Arm the start-bar picker. The chart is still live and untruncated;
     *  the next click on it chooses where the replay begins. */
    startReplaySelect(state, action: PayloadAction<{ tabId: string }>) {
      const { tabId } = action.payload;
      state.byTab[tabId] = {
        ...createDefaultReplayTabState(),
        speed: state.byTab[tabId]?.speed ?? 1,
        selecting: true,
      };
    },
    cancelReplaySelect(state, action: PayloadAction<{ tabId: string }>) {
      const t = state.byTab[action.payload.tabId];
      if (t) t.selecting = false;
    },
    /** Enter replay on `tabId`, parked on the bar at `cursorTime`. Keeps the
     *  tab's last speed so re-entering doesn't reset it to 1×. */
    enterReplay(
      state,
      action: PayloadAction<{
        tabId: string;
        cursorTime: number;
        entry: ReplayEntry;
      }>,
    ) {
      const { tabId, cursorTime, entry } = action.payload;
      state.byTab[tabId] = {
        ...createDefaultReplayTabState(),
        speed: state.byTab[tabId]?.speed ?? 1,
        selecting: false,
        active: true,
        cursorTime,
        startCursorTime: cursorTime,
        entry,
      };
    },
    exitReplay(state, action: PayloadAction<{ tabId: string }>) {
      const t = state.byTab[action.payload.tabId];
      if (!t) return;
      t.selecting = false;
      t.active = false;
      t.playing = false;
      t.cursorTime = null;
    },
    setReplayCursor(
      state,
      action: PayloadAction<{ tabId: string; cursorTime: number }>,
    ) {
      const t = state.byTab[action.payload.tabId];
      if (t?.active) t.cursorTime = action.payload.cursorTime;
    },
    setReplayPlaying(
      state,
      action: PayloadAction<{ tabId: string; playing: boolean }>,
    ) {
      const t = state.byTab[action.payload.tabId];
      if (t?.active) t.playing = action.payload.playing;
    },
    setReplaySpeed(
      state,
      action: PayloadAction<{ tabId: string; speed: ReplaySpeed }>,
    ) {
      const t = state.byTab[action.payload.tabId];
      if (t) t.speed = action.payload.speed;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeTab, (state, action) => {
        delete state.byTab[action.payload];
      })
      // Hydration replaces every tab's state from the server. Replay is not
      // part of that payload, so a hydrate must leave no cursor behind
      // pointing at a tab that now shows a different symbol or timeframe.
      .addCase(hydrateWorkspace, (state) => {
        state.byTab = {};
      });
  },
});

export const {
  startReplaySelect,
  cancelReplaySelect,
  enterReplay,
  exitReplay,
  setReplayCursor,
  setReplayPlaying,
  setReplaySpeed,
} = replaySlice.actions;
export default replaySlice.reducer;
